import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { DataTable } from "../components/DataTable";
import { Spinner } from "../components/Loader";
import { DateField } from "../components/DateField";
import { bookingService } from "../services/bookingService";
import { propertyService } from "../services/propertyService";
import { today } from "../utils/date";

export function Reports({ notify, tenantId }) {
  const [filters, setFilters] = useState({ start: today(-7), end: today(7), propertyId: "", roomNumber: "" });
  const [properties, setProperties] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function search() {
    setLoading(true);
    try {
      const allowedPropertyIds = filters.propertyId ? undefined : properties.map((property) => property.id);
      setRows(await bookingService.getReport(filters, allowedPropertyIds));
    } catch (err) {
      console.error("Reports search error:", err);
      notify?.(err?.message || "Could not load the report.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setProperties([]);
    setFilters((current) => ({ ...current, propertyId: "" }));
    propertyService.listForTenant(tenantId ?? null)
      .then((list) => {
        if (!cancelled) setProperties(list);
      })
      .catch((err) => {
        console.error("Reports properties error:", err);
        if (!cancelled) notify?.(err?.message || "Could not load properties.");
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => { search(); }, [tenantId, properties]);

  return (
    <section className="stack" aria-busy={loading || undefined}>
      <div className="toolbar">
        <label className="date-label">
          Start
          <DateField value={filters.start} onChange={(value) => setFilters({ ...filters, start: value })} showWeekday ariaLabel="Report start date" />
        </label>
        <label className="date-label">
          End
          <DateField value={filters.end} onChange={(value) => setFilters({ ...filters, end: value })} showWeekday ariaLabel="Report end date" />
        </label>
        <label>
          Property Name
          <select disabled={loading} value={filters.propertyId} onChange={(event) => setFilters({ ...filters, propertyId: event.target.value })}>
            <option value="">All Properties</option>
            {properties.map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}
          </select>
        </label>
        <label>Room Number<input disabled={loading} placeholder="Enter Room Number" value={filters.roomNumber} onChange={(event) => setFilters({ ...filters, roomNumber: event.target.value })} /></label>
        <button className="primary" disabled={loading} onClick={() => search()}>
          {loading ? <Spinner size={15} /> : <Search size={16} />}
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      <DataTable
        rows={rows}
        loading={loading}
        header="Reports"
        emptyMessage="No bookings found for these filters."
        fields={["id", "checkInDate", "checkOutDate", "propertyName", "roomNumber", "roomName", "guestName", "contact", "status", "totalAmount", "advancePaid", "remainingBalance"]}
      />
    </section>
  );
}
